/**
 * Service for dashboard API calls
 */

import { DashboardResponse, DashboardStatusResponse } from "../types/dashboard";
import { ColumnDefinition, DynamicTable, RowData } from "../types/dynamicTable";
import {
	DashboardMetricsResponse,
	ExtractionStatusResponse,
	MetricValueWithRationale,
	SourceReference,
} from "../types/extraction-v2";
import { apiFetch } from "../utils/apiClient";
import { getApiBaseURLFromEnv } from "../utils/env";
import { apiService } from "./api";

class DashboardService {
	private baseUrl = "/dashboard";
	private get v2BaseUrl() {
		// V2 APIs are at /api/v2, not under /api/v1
		return `${getApiBaseURLFromEnv()}/api/v2`;
	}

	/**
	 * Get dashboard data
	 */
	async getDashboard(dashboardId?: string): Promise<DashboardResponse> {
		const queryParams = dashboardId ? `?dashboard_id=${dashboardId}` : "";
		return await apiService.get<DashboardResponse>(
			`${this.baseUrl}${queryParams}`,
		);
	}

	/**
	 * Create a new dashboard
	 */
	async createDashboard(data: any): Promise<DashboardResponse> {
		return await apiService.post<DashboardResponse>(this.baseUrl, data);
	}

	/**
	 * Update a dashboard
	 */
	async updateDashboard(
		dashboardId: string,
		data: any,
	): Promise<DashboardResponse> {
		return await apiService.put<DashboardResponse>(
			`${this.baseUrl}/${dashboardId}`,
			data,
		);
	}

	/**
	 * Delete a dashboard
	 */
	async deleteDashboard(dashboardId: string): Promise<void> {
		await apiService.delete(`${this.baseUrl}/${dashboardId}`);
	}

	/**
	 * Get portfolio management data (V1 - client-based)
	 * @param mode - Dashboard mode: 'dynamic' (db only), 'template' (static only), 'auto' (db with template fallback)
	 * @param useCache - If false, assembles fresh dashboard from latest extraction. If true, uses cached dashboard.
	 */
	async getPortfolioData(
		mode?: "dynamic" | "template" | "auto",
		useCache?: boolean,
	): Promise<DashboardResponse> {
		const params = new URLSearchParams();
		if (mode) params.append("mode", mode);
		if (useCache !== undefined) params.append("use_cache", String(useCache));
		const queryParams = params.toString() ? `?${params.toString()}` : "";
		return await apiService.get<DashboardResponse>(
			`${this.baseUrl}/portfolio${queryParams}`,
		);
	}

	/**
	 * Poll dashboard processing status (V1 - client-based)
	 */
	async getPortfolioStatus(): Promise<DashboardStatusResponse> {
		return await apiService.get<DashboardStatusResponse>(
			`${this.baseUrl}/portfolio/status`,
		);
	}

	/**
	 * Get V2 extraction status for a project
	 */
	async getV2ExtractionStatus(
		projectId: string,
	): Promise<ExtractionStatusResponse> {
		const url = `${this.v2BaseUrl}/projects/${projectId}/extraction-status`;
		console.log(`[DashboardService] Fetching V2 status from: ${url}`);
		const response = await apiFetch(url);
		if (!response.ok) {
			throw new Error(`V2 status request failed: ${response.status}`);
		}
		return await response.json();
	}

	/**
	 * Get V2 dashboard metrics for a project
	 */
	async getV2DashboardMetrics(
		projectId: string,
	): Promise<DashboardMetricsResponse> {
		const url = `${this.v2BaseUrl}/projects/${projectId}/dashboard`;
		console.log(`[DashboardService] Fetching V2 dashboard from: ${url}`);
		const response = await apiFetch(url);
		if (!response.ok) {
			throw new Error(`V2 dashboard request failed: ${response.status}`);
		}
		return await response.json();
	}

	/**
	 * Get portfolio data (V2-only, requires projectId)
	 * @param projectId - Project ID for V2 extraction
	 * @param mode - Dashboard mode for backward compatibility (unused in V2)
	 * @param useCache - Cache setting for V1 fallback (unused in V2)
	 */
	async getHybridPortfolioData(projectId?: string): Promise<DashboardResponse> {
		// V2 only: require projectId and let errors propagate
		if (!projectId) {
			throw new Error("Project ID is required for V2 portfolio dashboard data");
		}

		console.log(`[DashboardService] Using V2 for project: ${projectId}`);

		// Get V2 dashboard data - will throw if not available
		const v2Metrics = await this.getV2DashboardMetrics(projectId);
		console.log(`[DashboardService] V2 dashboard retrieved successfully`);

		// Convert V2 metrics to V1 dashboard format
		return this.convertV2MetricsToDashboard(v2Metrics);
	}

	/**
	 * Get dashboard status
	 * @param projectId - Project ID for V2 extraction
	 */
	async getDashboardStatus(
		projectId: string,
	): Promise<DashboardStatusResponse> {
		console.log(
			`[DashboardService] fetching status for ProjectId: ${projectId}`,
		);
		const v2Status = await this.getV2ExtractionStatus(projectId);
		console.log(
			`[DashboardService] projectID ${projectId} status: ${v2Status.status}`,
		);

		// Convert V2 status to V1 format
		return this.convertV2StatusToDashboardStatus(v2Status);
	}

	/**
	 * Convert V2 extraction status to V1 dashboard status format
	 */
	private convertV2StatusToDashboardStatus(
		v2Status: ExtractionStatusResponse,
	): DashboardStatusResponse {
		return {
			status: v2Status.status,
			dashboard_id: v2Status.extraction_run_id?.toString(),
			extraction_run_id: v2Status.extraction_run_id,
			last_error: v2Status.error_message,
			updated_at: v2Status.completed_at
				? new Date(v2Status.completed_at).toISOString()
				: undefined,
		};
	}

	/**
	 * Convert V2 dashboard metrics to V1 dashboard format
	 * Takes judge_result.validated_metrics and converts to table format
	 */
	private convertV2MetricsToDashboard(
		v2Metrics: DashboardMetricsResponse,
	): DashboardResponse {
		// Metric display names mapping
		const metricDisplayNames: Record<string, string> = {
			quarter: "Quarter",
			year: "Year",
			qualified_cash: "Qualified Cash",
			total_cash: "Total Cash",
			revolver_outstanding: "Revolver Outstanding",
			revolver_commitment: "Revolver Commitment",
			first_lien_debt_excl_revolver: "First Lien Debt (excl. Revolver)",
			second_lien_debt: "Second Lien Debt",
			other_debt: "Other Debt",
			total_debt: "Total Debt",
			period_arr: "Period ARR",
			ltm_revenue_or_billings: "LTM Revenue or Billings",
			ltm_ebitda: "LTM EBITDA",
			ltm_interest_expense: "LTM Interest Expense",
			net_leverage_covenant: "Net Leverage Covenant",
			reported_net_leverage: "Reported Net Leverage",
		};

		// Metrics that should be formatted as currency
		const currencyMetrics = new Set([
			"qualified_cash",
			"total_cash",
			"revolver_outstanding",
			"revolver_commitment",
			"first_lien_debt_excl_revolver",
			"second_lien_debt",
			"other_debt",
			"total_debt",
			"period_arr",
			"ltm_revenue_or_billings",
			"ltm_ebitda",
			"ltm_interest_expense",
		]);

		// Define table columns
		const columns: ColumnDefinition[] = [
			{
				id: "metric",
				label: "Metric",
				key: "metric",
				data_type: "text",
				width: "15%",
				align: "left",
				is_sortable: false,
				is_frozen: false,
			},
			{
				id: "value",
				label: "Value",
				key: "value",
				data_type: "text",
				width: "10%",
				align: "left",
				is_sortable: false,
				is_frozen: false,
			},
			{
				id: "rationale",
				label: "Rationale",
				key: "rationale",
				data_type: "text",
				width: "50%",
				align: "left",
				is_sortable: false,
				is_frozen: false,
			},
			{
				id: "sources",
				label: "Source(s)",
				key: "sources",
				data_type: "text",
				width: "17%",
				align: "left",
				is_sortable: false,
				is_frozen: false,
			},
		];

		// Create rows from validated_metrics
		const rows: RowData[] = [];
		let rowOrder = 0;

		if (v2Metrics.metrics) {
			Object.entries(v2Metrics.metrics).forEach(
				([metricKey, metricData], index) => {
					// Type assertion for metric data structure
					const metric = metricData as MetricValueWithRationale;

					const displayName = metricDisplayNames[metricKey] || metricKey;
					const isCurrency = currencyMetrics.has(metricKey);

					// Format value
					let formattedValue = "—";
					if (metric.value !== null && metric.value !== undefined) {
						if (isCurrency) {
							formattedValue = new Intl.NumberFormat("en-US", {
								style: "currency",
								currency: "USD",
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
							}).format(metric.value);
						} else {
							formattedValue = metric.value.toString();
						}
					}

					// Build citation data from sources (multi-file) or legacy fields (single-file)
					let citationSources: Array<{
						source_id: string;
						file_name: string;
						file_type: "pdf";
						url: string;
						page?: number;
						file_id?: number;
						document_type?: string;
						fiscal_quarter?: string;
					}> = [];

					if (metric.sources && metric.sources.length > 0) {
						// Multi-file: LTM (4 quarters) or debt (audited + cert)
						citationSources = metric.sources
							.filter((src: SourceReference) => src.file_id && src.file_name)
							.map((src: SourceReference) => ({
								source_id: `file-${src.file_id}`,
								file_name: src.file_name!,
								file_type: "pdf" as const,
								url: "", // Will be populated on-demand
								page: src.page || undefined,
								file_id: src.file_id || undefined,
								document_type: src.document_type || undefined,
								fiscal_quarter: src.fiscal_quarter || undefined,
							}));
					} else if (metric.file_id && metric.file_name) {
						// Single-file: cert, loan metrics
						citationSources = [
							{
								source_id: `file-${metric.file_id}`,
								file_name: metric.file_name,
								file_type: "pdf" as const,
								url: "", // Will be populated on-demand
								page: metric.page || undefined,
								file_id: metric.file_id,
							},
						];
					}

					const citations =
						citationSources.length > 0
							? [
									{
										id: `citation-${metricKey}`,
										label: displayName,
										sources: citationSources,
									},
							  ]
							: undefined;

					// Build display values for page and attachments
					let pageDisplay = "—";
					let attachmentsDisplay = "—";

					if (metric.sources && metric.sources.length > 0) {
						// Multi-file: show all pages and file names
						const pages = metric.sources
							.filter((src: SourceReference) => src.page)
							.map((src: SourceReference) => src.page?.toString());
						pageDisplay = pages.length > 0 ? pages.join(", ") : "—";

						const fileNames = metric.sources
							.filter((src: SourceReference) => src.file_name)
							.map((src: SourceReference) => src.file_name);
						attachmentsDisplay =
							fileNames.length > 0 ? fileNames.join(", ") : "—";
					} else {
						// Single-file
						pageDisplay = metric.page?.toString() || "—";
						attachmentsDisplay = metric.file_name || "—";
					}

					rows.push({
						id: `metric-${metricKey}`,
						cells: {
							metric: displayName,
							value: formattedValue,
							rationale: metric.rationale || "—",
							sources:
								attachmentsDisplay !== "—"
									? `${attachmentsDisplay}${
											pageDisplay !== "—" ? ` (${pageDisplay})` : ""
									  }`
									: pageDisplay,
						},
						row_type: "normal",
						order: rowOrder++,
						citations,
						style: {
							font_size: "12px",
							font_weight: "400",
							line_height: "19px",
							text_color: "#282828",
							text_align: "left",
						},
					});
				},
			);
		}

		// Create the table
		const table: DynamicTable = {
			id: "v2-extracted-metrics",
			metadata: {
				title: "",
				description: "Financial metrics extracted and validated by LLM judge",
				table_type: "grid",
				layout: "vertical",
				is_editable: false,
				is_exportable: true,
			},
			columns,
			rows,
			config: {
				show_borders: true,
				show_header: true,
				show_row_numbers: false,
				striped_rows: true,
				hover_effect: true,
				compact_mode: false,
				sticky_header: true,
			},
			created_at: new Date(v2Metrics.extracted_at).toISOString(),
			updated_at: new Date(v2Metrics.extracted_at).toISOString(),
		};

		// Return dashboard with single section containing the table
		return {
			metadata: {
				company_name: "Portfolio Company",
				dashboard_title: "Portfolio Dashboard (V2 Extraction)",
				dashboard_description: `Quality Score: ${
					v2Metrics.quality_score
						? (v2Metrics.quality_score * 100).toFixed(1) + "%"
						: "N/A"
				}`,
				last_updated: new Date(v2Metrics.extracted_at).toISOString(),
				version: "2.0",
			},
			theme: {
				primary_color: "#004C4B",
				primary_bg: "rgba(0, 76, 75, 0.1)",
				secondary_color: "#6B7280",
				text_primary: "#282828",
				text_secondary: "#6B7280",
				background_white: "#FFFFFF",
				background_gray: "#F8F8F8",
				background_header: "#FAFAFA",
				border_color: "#E5E7EB",
				hover_bg: "#F9FAFB",
				icon_color: "#7E8083",
				font_family: '"Inter", "Inter Display"',
				font_size_xs: "12px",
				font_size_sm: "13px",
				font_size_base: "14px",
				font_size_lg: "18px",
				font_weight_normal: "400",
				font_weight_medium: "500",
				font_weight_semibold: "600",
				padding_sm: "5px 10px",
				padding_md: "10px 16px",
				padding_lg: "16px 20px",
				gap_sm: "8px",
				gap_md: "16px",
				gap_lg: "24px",
				border_radius: "8px",
				border_radius_sm: "5px",
				border_width: "1px",
			},
			sections: [
				{
					id: "financial-metrics",
					title: "Financial Metrics",
					description: "Validated metrics from V2 extraction",
					order: 0,
					layout: "vertical",
					tables: [table],
				},
			],
			created_at: new Date(v2Metrics.extracted_at).toISOString(),
		};
	}
}

export const dashboardService = new DashboardService();
export default dashboardService;
